import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Hook for detecting media query matches
 * @param query - Media query string (e.g., '(max-width: 1023px)')
 * @returns boolean indicating if the media query matches
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    
    // Set initial value
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    
    // Create listener for changes
    const listener = () => setMatches(media.matches);
    
    // Add listener
    media.addEventListener('change', listener);
    
    // Cleanup
    return () => media.removeEventListener('change', listener);
  }, [matches, query]);

  return matches;
};

/**
 * Convenience hooks for common breakpoints
 * Mobile breakpoint: < 1024px (matches Tailwind md: breakpoint)
 */

/**
 * Detects mobile devices (< 1024px)
 * @returns true if viewport width < 1024px
 */
export const useMobile = () => useMediaQuery('(max-width: 1023px)');

/**
 * Detects phone devices (< 768px)
 * @returns true if viewport width < 768px
 */
export const usePhone = () => useMediaQuery('(max-width: 767px)');

/**
 * Detects tablet devices (768px - 1023px)
 * @returns true if viewport width is between 768px and 1023px
 */
export const useTablet = () => useMediaQuery('(min-width: 768px) and (max-width: 1023px)');

/**
 * Detects desktop devices (>= 1024px)
 * @returns true if viewport width >= 1024px
 */
export const useDesktop = () => useMediaQuery('(min-width: 1024px)');

/**
 * Platform detection hooks using Capacitor
 */

/**
 * Detects if app is running as a native app (iOS/Android via Capacitor)
 * @returns true if running on native platform
 */
export const useIsNative = () => Capacitor.isNativePlatform();

/**
 * Detects if app is running on iOS
 * @returns true if platform is iOS
 */
export const useIsIOS = () => Capacitor.getPlatform() === 'ios';

/**
 * Detects if app is running on Android
 * @returns true if platform is Android
 */
export const useIsAndroid = () => Capacitor.getPlatform() === 'android';

/**
 * Interface for device context information
 */
export interface DeviceContext {
  /** True if viewport width < 1024px */
  isMobile: boolean;
  /** True if viewport width < 768px */
  isPhone: boolean;
  /** True if viewport width is 768px - 1023px */
  isTablet: boolean;
  /** True if viewport width >= 1024px */
  isDesktop: boolean;
  /** True if running as native app (Capacitor) */
  isNative: boolean;
  /** Platform: 'ios' | 'android' | 'web' */
  platform: string;
  /** True if platform is iOS */
  isIOS: boolean;
  /** True if platform is Android */
  isAndroid: boolean;
  /** True if device orientation is portrait */
  isPortrait: boolean;
  /** True if device orientation is landscape */
  isLandscape: boolean;
  /** True if user prefers reduced motion */
  prefersReducedMotion: boolean;
}

/**
 * Composite hook providing complete device context
 * Recommended for components that need multiple device checks
 * 
 * @example
 * const { isMobile, isNative, platform } = useDeviceContext();
 * if (isMobile && isNative) {
 *   // Mobile native app behavior
 * }
 */
export const useDeviceContext = (): DeviceContext => {
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const isPhone = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  return {
    isMobile,
    isPhone,
    isTablet,
    isDesktop,
    isNative: Capacitor.isNativePlatform(),
    platform: Capacitor.getPlatform(),
    isIOS: Capacitor.getPlatform() === 'ios',
    isAndroid: Capacitor.getPlatform() === 'android',
    isPortrait,
    isLandscape,
    prefersReducedMotion,
  };
};
